import moment from 'moment';
import * as validators from '@/modules/newTrip/validation';
import { createDateObject, } from '@/core';
import { errors as noErrors } from '../tripTemplates.state';

const setName = ({ commit, dispatch }, name) => {
	commit('setDetails', { path: 'name', value: name });
	dispatch('validateName');
};

const setDepartureTime = ({ state, commit, dispatch }, departureTime) => {
	commit('setDetails', { path: 'departureTime', value: moment(departureTime, 'LT').format('HH:mm') });
	if (state.templateDetails.legs.length === 1) {
		dispatch('setLegDepartureTime', { legNumber: 0, departureTime });
	}
	dispatch('validateTimes');
};

const setArrivalTime = ({ state, commit, dispatch }, arrivalTime) => {
	commit('setDetails', { path: 'arrivalTime', value: moment(arrivalTime, 'LT').format('HH:mm') });
	if (state.templateDetails.legs.length === 1) {
		dispatch('setLegArrivalTime', { legNumber: 0, arrivalTime });
	}
	dispatch('validateTimes');
};

const setDuration = ({ state, commit, dispatch }, duration) => {
	commit('setDetails', { path: 'duration', value: duration });
	if (state.templateDetails.legs.length === 1) {
		dispatch('setLegDuration', { legNumber: 0, duration });
	}
	dispatch('validateTimes');
};

const setStatus = ({ state, commit, dispatch }, status) => {
	commit('setDetails', { path: 'status', value: status });
	if (state.templateDetails.legs.length === 1) {
		dispatch('setLegStatus', { legNumber: 0, status });
	}
	if (status < 10) { // Reset all errors if setting draft/canceled status
		commit('setErrors', { path: '', value: noErrors() });
	}
};

const setType = ({ state, commit, dispatch }, value) => {
	commit('setDetails', { path: 'type', value: value });

	if ((value.internalCode || '').toUpperCase() === 'L') {
		if (!state.templateDetails.toAddress.id) {
			dispatch('setToAddress', state.templateDetails.fromAddress);
		} else if (!state.templateDetails.fromAddress.id) {
			dispatch('setFromAddress', state.templateDetails.toAddress);
		}
	}
	dispatch('validateFromAndToAddresses');
};

const setClerk = ({ commit, }, value) => {
	commit('setDetails', { path: 'clerk', value: value });
};

const setFromAddress = ({ state, commit, dispatch }, value) => {
	commit('setDetails', { path: 'fromAddress', value: value });
	commit('setDetails', { path: 'fromAddressId', value: value.id });
	if (state.templateDetails.legs.length === 1) {
		dispatch('setLegFromAddress', { legNumber: 0, ...value });
	}
	if ((state.templateDetails.type.internalCode || '').toUpperCase() === 'L'
		&& value.id
		&& !state.templateDetails.toAddress.id) {
		dispatch('setToAddress', value);
		// We don't validate here, because we know the validation
		// will be run by the setToAddress action.
	} else {
		dispatch('validateFromAndToAddresses');
	}
};

const setToAddress = ({ state, commit, dispatch }, value) => {
	commit('setDetails', { path: 'toAddress', value: value });
	commit('setDetails', { path: 'toAddressId', value: value.id });
	if (state.templateDetails.legs.length === 1) {
		dispatch('setLegToAddress', { legNumber: 0, ...value });
	}
	if ((state.templateDetails.type.internalCode || '').toUpperCase() === 'L'
		&& value.id
		&& !state.templateDetails.fromAddress.id) {
		dispatch('setFromAddress', value);
	} else {
		dispatch('validateFromAndToAddresses');
	}
};

const setCargoCarrier = ({ commit, dispatch }, cargoCarrier) => {
	if (!cargoCarrier || !Object.keys(cargoCarrier).length) {
		commit('setDetails', { path: 'cargoCarrier', value: undefined });
		commit('setDetails', { path: 'cargoCarrierId', value: undefined });
		return;
	}
	if (cargoCarrier.cargoCarrierType && cargoCarrier.cargoCarrierType.code.includes('L')) { // Cargo carrier can carry load.
		commit('setDetails', { path: 'cargoCarrier', value: cargoCarrier });
		commit('setDetails', { path: 'cargoCarrierId', value: cargoCarrier.id });
	} else {
		throw new Error('Invalid Cargo Carrier! This cargo carrier cannot carry any load.');
	}
	if (cargoCarrier.vendor) dispatch('setVendor', cargoCarrier.vendor);
	if (cargoCarrier.cargoCarrierType && cargoCarrier.cargoCarrierType.code === 'LB') { // Cargo carrier can transport itself.
		dispatch('setLegCargoCarrier', { legNumber: 0, ...cargoCarrier });
	}
};

const setVendor = ({ commit, dispatch }, value) => {
	let vendor = value || {};
	commit('setDetails', { path: 'vendor', value: vendor });
	commit('setDetails', { path: 'vendorId', value: vendor.id });
	dispatch('validateVendor');
};
/* ------- */

/* Validators */
const createValidatorAction = validator => (...params) => {
	let [{ state }] = params;
	if (!state.errors.isInvalid) return; // Only start validating single fields once errors are shown.
	if (state.templateDetails.status < 10) return; // Trip status is "draft" or "cancelled", so anything goes.
	validator(...params);
};

const hydrateWithEtdAndEta = (templateDetails, includeLegs) => ({
	...templateDetails,
	etd: templateDetails.departureTime
			&& (typeof templateDetails.departureTime === 'string')
			&& templateDetails.departureTime.includes(':')
		? createDateObject( { date: moment(), hasTime: true, time: templateDetails.departureTime })
		: null,
	eta: templateDetails.arrivalTime
			&& (typeof templateDetails.arrivalTime === 'string')
			&& templateDetails.arrivalTime.includes(':')
		? createDateObject( { date: moment().add(templateDetails.duration, 'd'), hasTime: true, time: templateDetails.arrivalTime })
		: null,
	...includeLegs && { legs: templateDetails.legs.map(
		leg => hydrateWithEtdAndEta(leg, false)
	) },
});

const validateName = ({ state, commit }) => {
	if (!state.templateDetails.name.length) {
		commit('setErrors', { path: 'name', value: 'nameNotSet' });
	} else if (state.tripTemplates
		.filter(tt => tt.id !== state.templateDetails.id)
		.map(tt => tt.name.toLowerCase())
		.includes(state.templateDetails.name.toLowerCase())) {
		commit('setErrors', { path: 'name', value: 'nameInUse' });
	} else {
		commit('setErrors', { path: 'name', value: false });
	}
};

const validateFromAndToAddresses = createValidatorAction(({ state, commit }) => {
	let allowEqualAddresses = Boolean(state.templateDetails.type.internalCode === 'L');
	let errors = validators.fromAndToAddresses(state.templateDetails, allowEqualAddresses);
	commit('setErrors', { path: 'fromAddress', value: errors.fromAddress });
	commit('setErrors', { path: 'toAddress', value: errors.toAddress });
});

const validateTimes = createValidatorAction(({ state, commit }) => {
	let errors = validators.etdAndEta(hydrateWithEtdAndEta(state.templateDetails, false));
	commit('setErrors', { path: 'etd', value: errors.etd });
	commit('setErrors', { path: 'eta', value: errors.eta });
});

const validateVendor = createValidatorAction(({ state, commit }) => {
	commit('setErrors', { path: 'vendor', value: validators.vendor(state.templateDetails.vendor) });
});

const validateAll = ({ state, dispatch, commit }, { validateDrivers=false }={}) => {
	if (state.templateDetails.status < 10) {
		return; // Trips status is "draft" or "cancelled", so anything goes!
	}
	let errors = validators.trip(hydrateWithEtdAndEta(state.templateDetails, true), { validateDrivers });
	commit('setErrors', { value: errors } );
	// Object.assign(state.errors, errors);
	dispatch('validateName');
};
/* ------- */

export default {
	setName,
	setDepartureTime,
	setArrivalTime,
	setDuration,
	setStatus,
	setType,
	setClerk,
	setFromAddress,
	setToAddress,
	setCargoCarrier,
	setVendor,
	validateName,
	validateFromAndToAddresses,
	validateTimes,
	validateVendor,
	validateAll,
};